import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, Users, Mail, Calendar, Image, MessageSquare, 
  DollarSign, Bell, LogOut, Menu, X, ChevronRight, Plus, Trash2, Edit, Upload, Eye, EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "@/lib/adminApi";
import SCAH_ADVOCACY_PRESET from "@/pages/admin/galleryPresets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth uses httpOnly cookies (axios.defaults.withCredentials is set in App.js).
// No bearer header is required for admin endpoints.

// ============ Dashboard Overview ============
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/dashboard/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  const statCards = [
    { label: "Total Members", value: stats?.members || 0, icon: Users, color: "text-teal-600" },
    { label: "Newsletter Subscribers", value: stats?.newsletter_subscribers || 0, icon: Mail, color: "text-amber-600" },
    { label: "Total Events", value: stats?.events || 0, icon: Calendar, color: "text-blue-600" },
    { label: "Total RSVPs", value: stats?.rsvps || 0, icon: ChevronRight, color: "text-purple-600" },
    { label: "Donations Received", value: stats?.donations || 0, icon: DollarSign, color: "text-green-600" },
    { label: "Donation Total", value: `$${(stats?.donation_total || 0).toFixed(2)}`, icon: DollarSign, color: "text-green-600" },
    { label: "Contact Messages", value: stats?.contacts || 0, icon: MessageSquare, color: "text-slate-600" },
  ];

  return (
    <div data-testid="dashboard-overview">
      <h1 className="text-2xl font-serif font-bold text-slate-900 mb-6">Dashboard Overview</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                </div>
                <stat.icon className={`w-10 h-10 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ============ Newsletter Subscribers ============
const SubscribersPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const response = await axios.get(`${API}/admin/subscribers`);
        setSubscribers(response.data.subscribers || []);
      } catch (error) {
        console.error("Error fetching subscribers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscribers();
  }, []);

  const activeCount = subscribers.filter(s => s.is_active).length;

  return (
    <div data-testid="subscribers-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Newsletter Subscribers</h1>
          <p className="text-slate-500">{activeCount} active subscribers</p>
        </div>
      </div>
      
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner"></div></div>
          ) : subscribers.length === 0 ? (
            <p className="text-center text-slate-500 py-12">No subscribers yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Name</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Subscribed</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub) => (
                    <tr key={sub.id} className="border-b hover:bg-slate-50">
                      <td className="p-4 text-sm">{sub.email}</td>
                      <td className="p-4 text-sm">{sub.first_name || "-"} {sub.last_name || ""}</td>
                      <td className="p-4 text-sm text-slate-500">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          sub.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {sub.is_active ? "Active" : "Unsubscribed"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ Events Management ============
const EventsManagementPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    image_url: ""
  });

  const fetchEvents = async () => {
    try {
      // Admin endpoint returns ALL events including hidden ones
      const response = await axios.get(`${API}/admin/events`);
      setEvents(response.data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, formData);
        toast.success("Event updated successfully");
      } else {
        await axios.post(`${API}/admin/events`, formData);
        toast.success("Event created successfully");
      }
      setDialogOpen(false);
      setEditingEvent(null);
      setFormData({ title: "", description: "", date: "", time: "", location: "", image_url: "" });
      fetchEvents();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success("Event deleted");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleToggleHidden = async (event) => {
    if (pendingToggle === event.id) return; // block double-clicks during in-flight PUT
    const newHidden = !event.hidden;
    setPendingToggle(event.id);
    // Optimistic update
    setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, hidden: newHidden } : e)));
    try {
      await axios.put(`${API}/admin/events/${event.id}`, { hidden: newHidden });
      toast.success(newHidden ? `"${event.title}" hidden from public` : `"${event.title}" is now visible`);
    } catch (error) {
      // Revert on failure
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, hidden: !newHidden } : e)));
      toast.error("Failed to update visibility");
    } finally {
      setPendingToggle(null);
    }
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      location: event.location,
      image_url: event.image_url || ""
    });
    setDialogOpen(true);
  };

  return (
    <div data-testid="events-management-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-serif font-bold text-slate-900">Events Management</h1>
        <Button onClick={() => { setEditingEvent(null); setFormData({ title: "", description: "", date: "", time: "", location: "", image_url: "" }); setDialogOpen(true); }} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </Button>
      </div>

      <div className="grid gap-4">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : events.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-500">No events yet</CardContent></Card>
        ) : (
          events.map((event) => (
            <Card
              key={event.id}
              className={event.hidden ? "opacity-70 border-dashed border-slate-300" : ""}
              data-testid={`event-card-${event.id}`}
            >
              <CardContent className="p-4 flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900">{event.title}</h3>
                    {event.hidden ? (
                      <Badge variant="secondary" className="bg-slate-200 text-slate-600" data-testid={`event-badge-hidden-${event.id}`}>
                        <EyeOff className="w-3 h-3 mr-1" /> Hidden
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`event-badge-visible-${event.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Public
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{event.date} at {event.time}</p>
                  <p className="text-sm text-slate-500">{event.location}</p>
                  <p className="text-xs text-teal-600 mt-1">{event.rsvp_count} RSVPs</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`event-toggle-label-${event.id}`}>
                    <span className="text-xs text-slate-600 min-w-[3.5rem] text-right">
                      {event.hidden ? "Hidden" : "Visible"}
                    </span>
                    <Switch
                      checked={!event.hidden}
                      disabled={pendingToggle === event.id}
                      onCheckedChange={() => handleToggleHidden(event)}
                      data-testid={`event-toggle-${event.id}`}
                      aria-label={event.hidden ? `Show ${event.title} publicly` : `Hide ${event.title} from public`}
                    />
                  </label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(event)} data-testid={`event-edit-btn-${event.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(event.id)} data-testid={`event-delete-btn-${event.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Event" : "Create Event"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
              </div>
              <div>
                <Label>Time</Label>
                <Input value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} placeholder="6:00 PM" required />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required />
            </div>
            <div>
              <Label>Image URL (optional)</Label>
              <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." />
            </div>
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
              {editingEvent ? "Update Event" : "Create Event"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Gallery Management ============
const GalleryManagementPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, ok: 0, fail: 0, failures: [] });
  const [formData, setFormData] = useState({ url: "", caption: "", category: "Events" });

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data || []);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/gallery`, formData);
      toast.success("Image added successfully");
      setDialogOpen(false);
      setFormData({ url: "", caption: "", category: "Events" });
      fetchGallery();
    } catch (error) {
      toast.error("Failed to add image");
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`);
      toast.success("Image deleted");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const loadPreset = () => {
    setBulkText(JSON.stringify(SCAH_ADVOCACY_PRESET, null, 2));
    toast.success(`Loaded ${SCAH_ADVOCACY_PRESET.length} SCAH advocacy photos`);
  };

  const handleBulkImport = async () => {
    let items;
    try {
      items = JSON.parse(bulkText);
    } catch (err) {
      toast.error("Invalid JSON — please check the format");
      return;
    }
    if (!Array.isArray(items) || items.length === 0) {
      toast.error("Provide a non-empty JSON array of {url, caption, category}");
      return;
    }
    const invalid = items.findIndex((it) => !it || typeof it.url !== "string" || typeof it.caption !== "string");
    if (invalid >= 0) {
      toast.error(`Item ${invalid + 1} is missing 'url' or 'caption'`);
      return;
    }

    setBulkBusy(true);
    setBulkProgress({ current: 0, total: items.length, ok: 0, fail: 0, failures: [] });

    let ok = 0;
    let fail = 0;
    const failures = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      try {
        await axios.post(`${API}/admin/gallery`, {
          url: it.url,
          caption: it.caption,
          category: it.category || "general",
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        failures.push(`#${i + 1}: ${err.response?.data?.detail || err.message}`);
      }
      setBulkProgress({ current: i + 1, total: items.length, ok, fail, failures: [...failures] });
    }

    setBulkBusy(false);
    if (fail === 0) {
      toast.success(`All ${ok} photos imported successfully`);
    } else {
      toast.error(`${ok} imported, ${fail} failed — see details below`);
    }
    fetchGallery();
  };

  const closeBulk = () => {
    if (bulkBusy) return; // prevent closing mid-upload
    setBulkOpen(false);
    setBulkText("");
    setBulkProgress({ current: 0, total: 0, ok: 0, fail: 0, failures: [] });
  };

  const bulkFailures = bulkProgress.failures;
  const bulkPercent = bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0;

  return (
    <div data-testid="gallery-management-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-serif font-bold text-slate-900">Gallery Management</h1>
        <div className="flex gap-2">
          <Button
            onClick={() => setBulkOpen(true)}
            variant="outline"
            className="border-amber-600 text-amber-700 hover:bg-amber-50"
            data-testid="bulk-import-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Import
          </Button>
          <Button onClick={() => setDialogOpen(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-image-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Image
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : images.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No images yet</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <Card key={img.id} className="overflow-hidden">
              <div className="aspect-square relative group">
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(img.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-medium truncate">{img.caption}</p>
                <p className="text-xs text-slate-500">{img.category}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Gallery Image</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Image URL</Label>
              <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://..." required />
            </div>
            <div>
              <Label>Caption</Label>
              <Input value={formData.caption} onChange={(e) => setFormData({ ...formData, caption: e.target.value })} required />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="Events, Advocacy, etc." />
            </div>
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">Add Image</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(open) => { if (!open) closeBulk(); else setBulkOpen(true); }}>
        <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Photos</DialogTitle>
            <DialogDescription>
              Paste a JSON array of <code className="text-xs bg-slate-100 px-1 rounded">{`{url, caption, category}`}</code> objects.
              Each photo will be added one-by-one. Already-imported photos can be deleted from the grid.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">JSON Array</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={loadPreset}
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                disabled={bulkBusy}
                data-testid="load-preset-btn"
              >
                Load SCAH 10-photo preset
              </Button>
            </div>
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={'[\n  {"url": "https://...", "caption": "...", "category": "advocacy"}\n]'}
              className="font-mono text-xs h-64"
              disabled={bulkBusy}
              data-testid="bulk-import-textarea"
            />

            {bulkProgress.total > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="bulk-progress">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-slate-700">
                    {bulkBusy ? "Importing..." : "Import complete"}
                  </span>
                  <span className="text-slate-600">
                    {bulkProgress.current} / {bulkProgress.total}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-amber-600 h-full transition-all"
                    style={{ width: `${bulkPercent}%` }}
                  />
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-emerald-700">✓ {bulkProgress.ok} succeeded</span>
                  {bulkProgress.fail > 0 && (
                    <span className="text-rose-700">✗ {bulkProgress.fail} failed</span>
                  )}
                </div>
                {bulkFailures.length > 0 && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 max-h-24 overflow-auto">
                    {bulkFailures.map((f, idx) => (
                      <div key={idx}>{f}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                onClick={handleBulkImport}
                disabled={bulkBusy || !bulkText.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="bulk-import-submit-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {bulkBusy ? `Importing ${bulkProgress.current}/${bulkProgress.total}...` : "Start Import"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeBulk}
                disabled={bulkBusy}
                data-testid="bulk-import-close-btn"
              >
                {bulkProgress.total > 0 && !bulkBusy ? "Done" : "Cancel"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Contacts Page ============
const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await axios.get(`${API}/admin/contacts`);
        setContacts(response.data.contacts || []);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []);

  return (
    <div data-testid="contacts-page">
      <h1 className="text-2xl font-serif font-bold text-slate-900 mb-6">Contact Submissions</h1>
      
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : contacts.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No contact submissions yet</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <Card key={contact.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-slate-900">{contact.subject}</h3>
                    <p className="text-sm text-slate-500">{contact.name} • {contact.email}</p>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-3 whitespace-pre-line">{contact.message}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Donations Page ============
const DonationsPage = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const response = await axios.get(`${API}/admin/donations`);
        setDonations(response.data.donations || []);
      } catch (error) {
        console.error("Error fetching donations:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDonations();
  }, []);

  return (
    <div data-testid="donations-page">
      <h1 className="text-2xl font-serif font-bold text-slate-900 mb-6">Donations</h1>
      
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner"></div></div>
          ) : donations.length === 0 ? (
            <p className="text-center text-slate-500 py-12">No donations yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Donor</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Amount</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((donation) => (
                    <tr key={donation.id} className="border-b hover:bg-slate-50">
                      <td className="p-4 text-sm text-slate-500">
                        {new Date(donation.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-sm">
                        {donation.donor_name || "Anonymous"}
                        {donation.donor_email && <span className="text-slate-400 block text-xs">{donation.donor_email}</span>}
                      </td>
                      <td className="p-4 text-sm font-medium">${donation.amount?.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          donation.payment_status === "paid" ? "bg-green-100 text-green-700" : 
                          donation.payment_status === "pending" ? "bg-yellow-100 text-yellow-700" : 
                          "bg-red-100 text-red-700"
                        }`}>
                          {donation.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ Notifications Page ============
const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/admin/notifications`);
        setNotifications(response.data.notifications || []);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  const typeLabels = {
    donation_receipt: "Donation Receipt",
    rsvp_confirmation: "RSVP Confirmation",
    newsletter_welcome: "Newsletter Welcome",
    contact_received: "Contact Received"
  };

  return (
    <div data-testid="notifications-page">
      <h1 className="text-2xl font-serif font-bold text-slate-900 mb-6">Notifications Queue</h1>
      <p className="text-slate-500 mb-4 text-sm">
        These are notification records. Connect an email service (e.g., SendGrid, Resend) to send actual emails.
      </p>
      
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : notifications.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No notifications yet</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notif) => (
            <Card key={notif.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded">
                      {typeLabels[notif.type] || notif.type}
                    </span>
                    <h3 className="font-semibold text-slate-900 mt-2">{notif.subject}</h3>
                    <p className="text-sm text-slate-500">To: {notif.recipient_email}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded ${
                      notif.status === "sent" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                    }`}>
                      {notif.status}
                    </span>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Main Admin Dashboard ============
const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/admin/me`);
        if (!cancelled) {
          setAdminInfo(response.data);
        }
      } catch {
        if (!cancelled) {
          navigate("/admin/login");
        }
      }
    };
    checkSession();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/subscribers", label: "Subscribers", icon: Mail },
    { path: "/admin/events", label: "Events", icon: Calendar },
    { path: "/admin/gallery", label: "Gallery", icon: Image },
    { path: "/admin/contacts", label: "Contacts", icon: MessageSquare },
    { path: "/admin/donations", label: "Donations", icon: DollarSign },
    { path: "/admin/notifications", label: "Notifications", icon: Bell },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 text-white p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-600 rounded flex items-center justify-center text-sm font-bold">SC</div>
          <span className="font-semibold">Admin</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
          <div className="p-6 border-b border-slate-800 hidden lg:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-600 rounded flex items-center justify-center font-bold text-white">SC</div>
              <div>
                <p className="font-semibold text-white">SCAH Admin</p>
                <p className="text-xs text-slate-400">{adminInfo?.name || "Administrator"}</p>
              </div>
            </div>
          </div>

          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path) ? "bg-amber-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white w-full">
              <LogOut className="w-5 h-5" />
              Logout
            </button>
            <Link to="/" className="flex items-center gap-3 px-4 py-2 text-slate-500 hover:text-slate-300 text-sm">
              ← Back to Website
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="subscribers" element={<SubscribersPage />} />
            <Route path="events" element={<EventsManagementPage />} />
            <Route path="gallery" element={<GalleryManagementPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="donations" element={<DonationsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
